/**
 * CloudParallaxSystem — multi-layer parallax sky with wind-driven cloud movement.
 *
 * Implements §4.6 of the design spec:
 *   - Three cloud layers (far/mid/near) with independent parallax depth and speed
 *   - Wind-driven movement via IWindProvider (WeatherSystem)
 *   - Atmospheric haze layer (distant horizon gradient)
 *   - Occasional airship passing in the deep background (Ghibli homage)
 *   - Cloud lifecycle: form (fade in), drift, dissipate (fade out)
 *   - Storm cloud mode: darkened tint, increased speed, heavier coverage
 *   - Cloud alignment mechanic: fires 'sky:alignment' on the scene EventEmitter
 *     when near-layer clouds align at solar noon on game day 7 (§4.6 secret #3)
 *   - Negative space preserved: clouds occupy upper 55% of viewport (§4.2)
 *
 * Performance: FAR layer uses SpriteGPULayer (zero CPU); MID/NEAR use Sprite tweens
 * (Phaser tween manager); update() detects wind changes only.
 *
 * Integrates with WeatherSystem:
 *   - Call setWindProvider() with WeatherSystem for wind-driven speed
 *   - Listen to WeatherSystem.EVENTS.CHANGE on the scene EventEmitter, then call
 *     applyWeather() to update cloud appearance and density
 */
import * as Phaser from 'phaser';
import type { IWindProvider } from './WindSystem';
import type { WeatherState } from '@game/shared';
import { isWebGL } from '../rendering/rendererUtils';

// ── Scene event fired for the cloud-alignment secret ──────────────────────────
/** Emitted on scene.events when the 7th-day noon alignment is active. */
export const SKY_ALIGNMENT_EVENT = 'sky:alignment';

// ── Textures ──────────────────────────────────────────────────────────────────
const CLOUD_FAR_KEY      = '__cloud_far__';
const CLOUD_MID_KEY      = '__cloud_mid__';
const CLOUD_NEAR_KEY     = '__cloud_near__';
const CLOUD_STORM_KEY    = '__cloud_storm__';
const AIRSHIP_KEY        = '__cloud_airship__';

// ── Geometry ──────────────────────────────────────────────────────────────────
/** Sky zone: top fraction of the viewport that clouds can occupy (§4.2) */
const SKY_Y_MAX_FRAC   = 0.55;
/** Fraction of sky allocated to haze (bottom edge of sky zone) */
const HAZE_Y_FRAC      = 0.45;

// ── Depth ─────────────────────────────────────────────────────────────────────
const DEPTH_HAZE        =  2;   // below everything — pure atmosphere
const DEPTH_FAR_CLOUD   =  5;
const DEPTH_AIRSHIP     = 10;
const DEPTH_MID_CLOUD   = 18;
const DEPTH_NEAR_CLOUD  = 30;

// ── Cloud counts ──────────────────────────────────────────────────────────────
const FAR_COUNT_CLEAR   = 16;  // GPU layer
const FAR_COUNT_STORM   = 22;
const MID_COUNT_CLEAR   =  5;
const MID_COUNT_STORM   =  8;
const NEAR_COUNT_CLEAR  =  3;
const NEAR_COUNT_STORM  =  5;

// ── Speed (px/s) ─────────────────────────────────────────────────────────────
/** Base horizontal drift with no wind */
const BASE_SPEED: Record<'far' | 'mid' | 'near', number> = {
  far:  6,
  mid: 13,
  near: 22,
};
/** Extra px/s added per unit of wind intensity [0..1] */
const WIND_BOOST: Record<'far' | 'mid' | 'near', number> = {
  far:  14,
  mid:  30,
  near: 50,
};

// ── Parallax scroll factors ───────────────────────────────────────────────────
const SCROLL_FAR   = 0.06;
const SCROLL_MID   = 0.18;
const SCROLL_NEAR  = 0.34;

// ── Tints ────────────────────────────────────────────────────────────────────
const TINT_CLEAR   = 0xffffff;
const TINT_RAIN    = 0xd0d8e0;  // pale blue-gray
const TINT_STORM   = 0x889098;  // dark slate
const TINT_HAZE    = 0xd6eaf8;  // sky blue haze

// ── Airship ──────────────────────────────────────────────────────────────────
const AIRSHIP_INTERVAL_MIN_MS = 3 * 60_000;   // 3 min
const AIRSHIP_INTERVAL_MAX_MS = 8 * 60_000;   // 8 min
const AIRSHIP_SPEED_PX_S      = 28;           // drifts left across sky
const AIRSHIP_Y_FRAC          = 0.08;         // high in the sky
const AIRSHIP_ALPHA           = 0.55;

// ── Lifecycle ─────────────────────────────────────────────────────────────────
const FADE_IN_MS    = 2_200;
const FADE_OUT_MS   = 1_800;

// ── Wind change detection ─────────────────────────────────────────────────────
const WIND_SPEED_REBUILD_THRESHOLD = 0.1;

// ─── Types ────────────────────────────────────────────────────────────────────

type CloudLayer = 'far' | 'mid' | 'near';

interface ActiveCloud {
  sprite: Phaser.GameObjects.Sprite;
  driftTween: Phaser.Tweens.Tween;
  layer: CloudLayer;
  /** Target alpha when fully formed */
  targetAlpha: number;
}

// ─── DefaultWindProvider (calm) ───────────────────────────────────────────────
class DefaultWindProvider implements IWindProvider {
  getWindDirection() { return Math.PI + 0.2; }
  getWindIntensity() { return 0.2; }
}

// ─── CloudParallaxSystem ──────────────────────────────────────────────────────

type MemberAnim = Phaser.Types.GameObjects.SpriteGPULayer.MemberAnimation;

export class CloudParallaxSystem {
  private readonly scene: Phaser.Scene;
  private provider: IWindProvider = new DefaultWindProvider();
  private weather: WeatherState   = 'clear';

  // GPU far layer
  private farLayer: Phaser.GameObjects.SpriteGPULayer | null = null;

  // CPU mid + near clouds
  private midClouds:  ActiveCloud[] = [];
  private nearClouds: ActiveCloud[] = [];

  // Haze
  private hazeRect: Phaser.GameObjects.Rectangle | null = null;

  // Airship
  private airshipSprite: Phaser.GameObjects.Sprite | null = null;
  private airshipTimer: Phaser.Time.TimerEvent | null = null;

  // Orphan timer tracking (AGE-708: prevent timer leaks over long sessions)
  private pendingTimers: Phaser.Time.TimerEvent[] = [];

  // Wind change detection
  private lastWindSpeed = -1;  // computed as dir-adjusted effective speed
  private active = false;

  // Rebuild cooldown — prevents rapid successive GPU layer rebuilds during weather transitions
  private _lastRebuildTime = 0;
  private _elapsedTime = 0;

  // Alignment tracking
  private alignmentActive = false;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  // ── Public API ──────────────────────────────────────────────────────────────

  /** Wire in the weather system wind provider. */
  setWindProvider(provider: IWindProvider): void {
    this.provider = provider;
  }

  /**
   * Apply a new weather state. Rebuilds cloud layers with appropriate tint,
   * density, and speed for the new conditions.
   */
  applyWeather(state: WeatherState): void {
    this.weather = state;
    if (this.active) this.rebuild();
  }

  /** Activate cloud system (outdoor scenes only). */
  activate(): void {
    if (this.active) return;
    this.active = true;
    this.buildHaze();
    this.buildFarLayer();
    this.spawnInitialClouds('mid');
    this.spawnInitialClouds('near');
    this.scheduleNextAirship();
  }

  /** Deactivate cloud system (interiors). */
  deactivate(): void {
    if (!this.active) return;
    this.active = false;
    this.teardown();
  }

  /**
   * Per-frame update. Detects meaningful wind speed changes and rebuilds
   * GPU layer accordingly. All sprite drift runs via Phaser tweens — no per-frame
   * per-cloud math in steady state.
   */
  update(delta: number): void {
    if (!this.active) return;

    this._elapsedTime += delta;

    const speed = this.effectiveSpeed('mid');
    if (Math.abs(speed - this.lastWindSpeed) > WIND_SPEED_REBUILD_THRESHOLD * BASE_SPEED.mid) {
      // Cooldown: skip rebuild if less than 2s since last one
      if (this._elapsedTime - this._lastRebuildTime < 2000) return;
      this._lastRebuildTime = this._elapsedTime;
      this.lastWindSpeed = speed;
      // Rebuild GPU far layer for new wind; existing CPU clouds will tween out naturally
      this.buildFarLayer();
    }
  }

  /**
   * Check cloud alignment for the secret mechanic (§4.6, secret #3).
   * Call each in-game tick with current day number and time-of-day ms.
   * Fires `SKY_ALIGNMENT_EVENT` on scene.events when active (day 7, noon ±15 min).
   *
   * TODO(B-ui-c5): still uncalled. The day number now exists (see
   * `src/ui/c5/dayCount.ts`), but this method's `dayTimeMs` is scaled to a
   * 24 h real day (noon = 43_200_000) while the project's day is 20 min
   * (`DAY_DURATION_MS`). Wiring it needs a deliberate decision on that
   * mapping and on how wide the alignment window should feel at 20 min/day —
   * out of scope for the C5 frame, which only owns the display of the day.
   *
   * @param gameDay     Current in-game day number (1-indexed)
   * @param dayTimeMs   Time elapsed in current day, ms (0 = midnight, 43200000 = noon)
   */
  checkAlignment(gameDay: number, dayTimeMs: number): void {
    const NOON_MS = 43_200_000;
    const WINDOW_MS = 15 * 60_000;
    const isDay7Noon = gameDay === 7 && Math.abs(dayTimeMs - NOON_MS) < WINDOW_MS;

    if (isDay7Noon && !this.alignmentActive) {
      this.alignmentActive = true;
      this.scene.events.emit(SKY_ALIGNMENT_EVENT, { active: true });
    } else if (!isDay7Noon && this.alignmentActive) {
      this.alignmentActive = false;
      this.scene.events.emit(SKY_ALIGNMENT_EVENT, { active: false });
    }
  }

  /** Full teardown — call from scene shutdown. */
  destroy(): void {
    this.active = false;
    this.teardown();
  }

  // ── Private: core setup ─────────────────────────────────────────────────────

  private rebuild(): void {
    this.teardown();
    this.buildHaze();
    this.buildFarLayer();
    this.spawnInitialClouds('mid');
    this.spawnInitialClouds('near');
    this.scheduleNextAirship();
  }

  private teardown(): void {
    // GPU layer
    if (this.farLayer) { this.farLayer.destroy(); this.farLayer = null; }

    // CPU mid + near
    for (const c of [...this.midClouds, ...this.nearClouds]) {
      c.driftTween.destroy();
      c.sprite.destroy();
    }
    this.midClouds.length  = 0;
    this.nearClouds.length = 0;

    // Haze
    if (this.hazeRect) { this.hazeRect.destroy(); this.hazeRect = null; }

    // Airship
    if (this.airshipTimer)  { this.airshipTimer.destroy();  this.airshipTimer  = null; }
    if (this.airshipSprite) { this.airshipSprite.destroy(); this.airshipSprite = null; }

    // Orphan timers (dissipation + airship fade)
    for (const t of this.pendingTimers) t.destroy();
    this.pendingTimers.length = 0;
  }

  // ── Private: haze ───────────────────────────────────────────────────────────

  private buildHaze(): void {
    const { width, height } = this.scene.cameras.main;
    this.hazeRect = this.scene.add.rectangle(
      0, height * HAZE_Y_FRAC,
      width, height * (1 - HAZE_Y_FRAC),
      TINT_HAZE,
      0.06,
    );
    this.hazeRect.setOrigin(0, 0);
    this.hazeRect.setScrollFactor(0);
    this.hazeRect.setDepth(DEPTH_HAZE);
    (this.scene as any).addToUI?.(this.hazeRect);
  }

  // ── Private: GPU far layer ──────────────────────────────────────────────────

  private ensureFarTexture(): void {
    if (this.scene.textures.exists(CLOUD_FAR_KEY)) return;
    // Small wispy cloud: 4 overlapping circles in a wide-horizontal arrangement
    const w = 48, h = 20;
    const gfx = this.scene.make.graphics({}, false);
    gfx.fillStyle(0xffffff, 1);
    gfx.fillCircle(24, 12, 10);
    gfx.fillCircle(10, 14, 7);
    gfx.fillCircle(38, 14, 7);
    gfx.fillCircle(17, 10, 8);
    gfx.fillCircle(32, 10, 8);
    const rt = this.scene.add.renderTexture(0, 0, w, h);
    rt.draw(gfx, 0, 0);
    rt.render();
    rt.saveTexture(CLOUD_FAR_KEY);
    rt.destroy(); gfx.destroy();
  }

  private buildFarLayer(): void {
    if (this.farLayer) { this.farLayer.destroy(); this.farLayer = null; }
    if (!isWebGL(this.scene)) return;
    this.ensureFarTexture();

    const cam = this.scene.cameras.main;
    const vw  = cam.width;
    const vh  = cam.height;

    const count     = this.weather === 'storm' ? FAR_COUNT_STORM : FAR_COUNT_CLEAR;
    const speedPxS  = this.effectiveSpeed('far');
    const driftMs   = speedPxS > 0.5 ? (vw / speedPxS) * 1000 : 0;
    const tint      = this.cloudTint();

    const texture   = this.scene.textures.get(CLOUD_FAR_KEY);
    this.farLayer   = this.scene.add.spriteGPULayer(texture, count);
    this.farLayer.setDepth(DEPTH_FAR_CLOUD);

    for (let i = 0; i < count; i++) {
      const startX    = Math.random() * vw;
      // Keep within upper 55% of viewport (sky zone)
      const startY    = Math.random() * vh * SKY_Y_MAX_FRAC;
      const scale     = 0.4 + Math.random() * 0.5;
      const baseAlpha = this.weather === 'storm' ? 0.3 + Math.random() * 0.2 : 0.15 + Math.random() * 0.15;

      const xVal: number | MemberAnim = driftMs > 0
        ? { base: startX, amplitude: -vw, ease: 'Linear', duration: driftMs, loop: true }
        : startX;

      this.farLayer.addMember({
        x: xVal,
        y: startY,
        scaleX: scale * 1.4, // wider than tall
        scaleY: scale,
        rotation: 0,
        originX: 0.5,
        originY: 0.5,
        alpha: {
          base: baseAlpha,
          amplitude: baseAlpha * 0.3,
          ease: 'Sine.easeInOut',
          duration: 4000 + Math.random() * 3000,
          loop: true,
          yoyo: true,
          delay: Math.random() * 4000,
        },
        tintTopLeft:    tint,
        tintTopRight:   tint,
        tintBottomLeft: tint,
        tintBottomRight: tint,
        tintBlend: 1,
        tintMode:  0,
        scrollFactorX: SCROLL_FAR,
        scrollFactorY: 0,
        frame: 0,
      });
    }
  }

  // ── Private: CPU mid + near cloud lifecycle ─────────────────────────────────

  private ensureCloudTexture(key: string, w: number, h: number, circles: [number, number, number][]): void {
    if (this.scene.textures.exists(key)) return;
    const gfx = this.scene.make.graphics({}, false);
    gfx.fillStyle(0xffffff, 1);
    for (const [cx, cy, r] of circles) gfx.fillCircle(cx, cy, r);
    const rt = this.scene.add.renderTexture(0, 0, w, h);
    rt.draw(gfx, 0, 0);
    rt.render();
    rt.saveTexture(key);
    rt.destroy(); gfx.destroy();
  }

  private ensureMidTexture(): void {
    this.ensureCloudTexture(CLOUD_MID_KEY, 120, 50, [
      [60, 30, 22], [35, 35, 17], [88, 35, 17],
      [48, 22, 18], [74, 22, 18], [22, 38, 13], [100, 38, 13],
    ]);
  }

  private ensureNearTexture(): void {
    this.ensureCloudTexture(CLOUD_NEAR_KEY, 200, 80, [
      [100, 48, 34], [60, 55, 26], [145, 55, 26],
      [78, 36, 28], [125, 36, 28], [38, 58, 20], [165, 58, 20], [105, 28, 22],
    ]);
  }

  private ensureStormTexture(): void {
    // Dense, heavy storm cloud — same shape as mid but drawn once, tinted at runtime
    this.ensureCloudTexture(CLOUD_STORM_KEY, 160, 65, [
      [80, 38, 28], [48, 45, 22], [115, 45, 22],
      [62, 28, 22], [100, 28, 22], [30, 50, 16], [132, 50, 16], [80, 22, 18],
    ]);
  }

  private cloudTextureKey(layer: CloudLayer): string {
    if (this.weather === 'storm') return CLOUD_STORM_KEY;
    return layer === 'near' ? CLOUD_NEAR_KEY : CLOUD_MID_KEY;
  }

  private spawnInitialClouds(layer: CloudLayer): void {
    this.ensureMidTexture();
    this.ensureNearTexture();
    this.ensureStormTexture();

    const count = this.cloudCount(layer);
    const cam = this.scene.cameras.main;

    for (let i = 0; i < count; i++) {
      // On initial spawn scatter them across the viewport (not all queued at left edge)
      const startX = Math.random() * cam.width;
      this.spawnCloud(layer, startX, true);
    }
  }

  private spawnCloud(layer: CloudLayer, startX: number, scattered = false): void {
    const cam   = this.scene.cameras.main;
    const vw    = cam.width;
    const vh    = cam.height;

    const texKey = this.cloudTextureKey(layer);
    const sprite = this.scene.add.sprite(startX, 0, texKey);

    const targetAlpha = this.weather === 'storm'
      ? (layer === 'near' ? 0.80 : 0.60)
      : (layer === 'near' ? 0.70 : 0.50);

    // Keep clouds in upper sky zone (§4.2: generous sky / negative space)
    const yZoneMax = vh * SKY_Y_MAX_FRAC;
    const yZoneMin = vh * 0.02;
    sprite.y = yZoneMin + Math.random() * (yZoneMax - yZoneMin);

    const scale = layer === 'near'
      ? 0.7 + Math.random() * 0.5
      : 0.5 + Math.random() * 0.4;
    sprite.setScale(scale);
    sprite.setAlpha(scattered ? targetAlpha * Math.random() : 0); // skip fade-in for initial scatter
    sprite.setTint(this.cloudTint());
    sprite.setScrollFactor(layer === 'near' ? SCROLL_NEAR : SCROLL_MID, 0);
    sprite.setDepth(layer === 'near' ? DEPTH_NEAR_CLOUD : DEPTH_MID_CLOUD);

    // Fade in (skip for initially scattered clouds)
    if (!scattered) {
      this.scene.tweens.add({
        targets: sprite,
        alpha: targetAlpha,
        duration: FADE_IN_MS,
        ease: 'Sine.easeIn',
      });
    }

    // Drift tween: cloud moves left across viewport
    const speedPxS   = this.effectiveSpeed(layer);
    const cloudWidth  = sprite.displayWidth;
    const distance    = vw - startX + cloudWidth * 2; // from startX to off-screen right
    const durationMs  = speedPxS > 0.5 ? (distance / speedPxS) * 1000 : 999_999_999;

    const driftTween = this.scene.tweens.add({
      targets:  sprite,
      x:        -cloudWidth,
      duration: durationMs,
      ease:     'Linear',
      onComplete: () => this.onCloudComplete(cloud),
    });

    // Occasionally dissipate mid-journey (10% chance per MID cloud)
    const maybeDissipate = layer === 'mid' && Math.random() < 0.10;
    if (maybeDissipate) {
      const dissipateAt = 0.3 + Math.random() * 0.5;
      const timer = this.scene.time.delayedCall(durationMs * dissipateAt, () => {
        if (!sprite.active) return;
        driftTween.pause();
        this.scene.tweens.add({
          targets: sprite,
          alpha: 0,
          duration: FADE_OUT_MS,
          ease: 'Sine.easeOut',
          onComplete: () => {
            if (cloud) this.removeCloud(cloud);
            sprite.destroy();
          },
        });
      });
      this.pendingTimers.push(timer);
    }

    const cloud: ActiveCloud = { sprite, driftTween, layer, targetAlpha };

    if (layer === 'mid') {
      this.midClouds.push(cloud);
    } else {
      this.nearClouds.push(cloud);
    }
  }

  private onCloudComplete(cloud: ActiveCloud): void {
    this.removeCloud(cloud);
    cloud.sprite.destroy();

    // Replace with a new cloud entering from the right edge
    if (this.active) {
      const cam = this.scene.cameras.main;
      const spawnX = cam.width + 20 + Math.random() * 60;
      this.spawnCloud(cloud.layer, spawnX);
    }
  }

  private removeCloud(cloud: ActiveCloud): void {
    const arr = cloud.layer === 'mid' ? this.midClouds : this.nearClouds;
    const i   = arr.indexOf(cloud);
    if (i !== -1) arr.splice(i, 1);
  }

  // ── Private: airship ────────────────────────────────────────────────────────

  private ensureAirshipTexture(): void {
    if (this.scene.textures.exists(AIRSHIP_KEY)) return;
    // Simple silhouette: elongated hull + small gondola + propeller circles
    const w = 80, h = 30;
    const gfx = this.scene.make.graphics({}, false);
    gfx.fillStyle(0x334455, 1);
    gfx.fillEllipse(40, 12, 70, 18);   // main gas bag
    gfx.fillRect(28, 19, 24, 5);       // gondola
    gfx.fillCircle(20, 15, 5);         // port propeller disk
    gfx.fillCircle(60, 15, 5);         // starboard propeller disk
    const rt = this.scene.add.renderTexture(0, 0, w, h);
    rt.draw(gfx, 0, 0);
    rt.render();
    rt.saveTexture(AIRSHIP_KEY);
    rt.destroy(); gfx.destroy();
  }

  private scheduleNextAirship(): void {
    const delay = AIRSHIP_INTERVAL_MIN_MS
      + Math.random() * (AIRSHIP_INTERVAL_MAX_MS - AIRSHIP_INTERVAL_MIN_MS);

    this.airshipTimer = this.scene.time.delayedCall(delay, () => {
      if (this.active) {
        this.launchAirship();
        this.scheduleNextAirship();
      }
    });
  }

  private launchAirship(): void {
    if (this.airshipSprite) return; // one at a time

    this.ensureAirshipTexture();
    const cam = this.scene.cameras.main;
    const vw  = cam.width;
    const vh  = cam.height;

    // Enter from right, exit left; y is high in the sky
    const startX   = vw + 50;
    const endX     = -100;
    const y        = vh * AIRSHIP_Y_FRAC;
    const distance = startX - endX;
    const duration = (distance / AIRSHIP_SPEED_PX_S) * 1000;

    this.airshipSprite = this.scene.add.sprite(startX, y, AIRSHIP_KEY);
    this.airshipSprite.setAlpha(0);
    this.airshipSprite.setScrollFactor(0.08, 0); // very gentle parallax
    this.airshipSprite.setDepth(DEPTH_AIRSHIP);
    this.airshipSprite.setScale(1.2 + Math.random() * 0.4);

    // Fade in, drift, fade out
    this.scene.tweens.add({ targets: this.airshipSprite, alpha: AIRSHIP_ALPHA, duration: 2000 });
    this.scene.tweens.add({
      targets:  this.airshipSprite,
      x:        endX,
      duration,
      ease:     'Linear',
      onComplete: () => {
        this.airshipSprite?.destroy();
        this.airshipSprite = null;
      },
    });
    // Fade out 12 s before exit
    const fadeStart = Math.max(0, duration - 12_000);
    const fadeTimer = this.scene.time.delayedCall(fadeStart, () => {
      if (this.airshipSprite) {
        this.scene.tweens.add({ targets: this.airshipSprite, alpha: 0, duration: 3000 });
      }
    });
    this.pendingTimers.push(fadeTimer);
  }

  // ── Private: helpers ─────────────────────────────────────────────────────────

  /** Effective drift speed px/s for a layer, incorporating wind. */
  private effectiveSpeed(layer: CloudLayer): number {
    const intensity = this.provider.getWindIntensity();
    return BASE_SPEED[layer] + WIND_BOOST[layer] * intensity;
  }

  private cloudCount(layer: CloudLayer): number {
    const storm = this.weather === 'storm';
    if (layer === 'mid')  return storm ? MID_COUNT_STORM  : MID_COUNT_CLEAR;
    if (layer === 'near') return storm ? NEAR_COUNT_STORM : NEAR_COUNT_CLEAR;
    return storm ? FAR_COUNT_STORM : FAR_COUNT_CLEAR;
  }

  private cloudTint(): number {
    switch (this.weather) {
      case 'storm': return TINT_STORM;
      case 'rain':  return TINT_RAIN;
      default:      return TINT_CLEAR;
    }
  }
}
